import type { ExperimentConfig } from '@/types';

/**
 * Experiment 9: Volumetric Analysis of Acetic Acid in Commercial Vinegar
 * CHEM-1131 Laboratory
 *
 * Students perform acid-base titration to determine the percent acetic acid
 * in commercial vinegar using standardized NaOH solution.
 */
const experiment9Config: ExperimentConfig = {
  id: 'exp9',
  number: 9,
  name: 'Volumetric Analysis of Acetic Acid',
  description: 'Determine the percent acetic acid in commercial vinegar and the concentration of glacial acetic acid using acid-base titration with ~0.10 M NaOH (previously standardized in analytical techniques) and phenolphthalein indicator.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Perform accurate acid-base titrations',
    'Use a burette with proper technique',
    'Detect the titration endpoint using phenolphthalein',
    'Calculate analyte concentration from titration data',
    'Understand dilution factors in quantitative analysis'
  ],
  safetyNotes: [
    'NaOH is corrosive - avoid skin contact',
    'Glacial acetic acid is corrosive and has strong fumes',
    'Wear goggles at all times during titration',
    'Clean up spills immediately with water',
    'Disposal: Glacial acetic acid must be disposed of properly (see Appendix G)'
  ],
  requiredEquipment: [
    '50 mL burette',
    'Burette clamp and stand',
    '10 mL volumetric pipette (for sample transfer and aliquots)',
    '100 mL volumetric flask',
    '250 mL Erlenmeyer flasks (3)',
    'Beakers (150 mL, 2 — for waste/rinses)',
    'Pipette bulb or filler',
    'White paper or tile (under flask for endpoint detection)',
    'Wash bottle',
    'Funnel',
    'Phenolphthalein indicator (1% in ethanol)'
  ],
  parts: [
    {
      id: 1,
      name: 'vinegar_preparation',
      title: 'Part I: Vinegar Sample Preparation',
      equipment: ['10 mL volumetric pipette', '100 mL volumetric flask', 'Commercial vinegar'],
      initialState: {
        sampleType: 'vinegar',
        sampleVolume: null,
        dilutionComplete: false,
        solutionColor: '#FFF8E7'
      },
      steps: [
        {
          id: 1,
          title: 'Select Sample',
          shortDescription: 'Obtain commercial vinegar sample',
          handler: 'handleSelectSample',
          infoContent: {
            title: 'Acetic Acid in Vinegar',
            content: 'Commercial vinegar typically contains 4-6% acetic acid by mass. We will determine the exact percentage through titration with standardized NaOH.',
            whatHappens: 'A commercial vinegar sample is selected for analysis; its acetic acid content will be determined by titration with standardized NaOH.',
            observe: [
              'Note the vinegar brand and any label claims about acidity',
              'The vinegar has a characteristic amber color and sour smell',
              'Record the sample identification for your lab report',
            ],
          }
        },
        {
          id: 2,
          title: 'Pipette Vinegar',
          shortDescription: 'Transfer exactly 10.00 mL of vinegar to volumetric flask',
          handler: 'handlePipetteSample',
          infoContent: {
            title: 'Volumetric Pipette',
            content: 'The volumetric pipette delivers exactly 10.00 mL. Allow the last drop to drain by touching the tip to the flask wall - do not blow out.',
            whatHappens: 'Exactly 10.00 mL of vinegar is transferred to the volumetric flask using a volumetric pipette with proper draining technique.',
            observe: [
              'Fill the pipette above the mark, then drain to the calibration line',
              'Touch the tip to the flask wall to deliver the last drop',
              'Do not blow out the remaining liquid in the pipette tip',
            ],
          }
        },
        {
          id: 3,
          title: 'Dilute to Volume',
          shortDescription: 'Dilute to the mark with freshly boiled and cooled deionised water',
          handler: 'handleDiluteSample',
          infoContent: {
            title: 'Dilution',
            content: 'Dilute to the 100 mL mark with freshly boiled and cooled deionised water. Boiling removes dissolved CO₂ which could affect the titration. The dilution factor is 10.',
            whatHappens: 'The vinegar sample is diluted to 100 mL with CO2-free deionized water, creating a 1:10 dilution to bring the acetic acid concentration into a suitable titration range.',
            observe: [
              'Use freshly boiled and cooled deionized water (CO2-free)',
              'Fill to the 100 mL mark, reading the meniscus at eye level',
              'The dilution factor of 10 must be accounted for in calculations',
            ],
          }
        },
        {
          id: 4,
          title: 'Mix Thoroughly',
          shortDescription: 'Invert the flask several times to ensure complete mixing',
          handler: 'handleMixSolution'
        }
      ]
    },
    {
      id: 2,
      name: 'burette_setup',
      title: 'Part II: Burette Preparation',
      equipment: ['50 mL burette', 'Burette clamp', 'Funnel', '~0.10 M NaOH solution (previously standardized)'],
      initialState: {
        buretteFilled: false,
        buretteReading: 0,
        stopcockPrimed: false
      },
      steps: [
        {
          id: 1,
          title: 'Rinse Burette',
          shortDescription: 'Rinse burette first with water, then with NaOH solution',
          handler: 'handleRinseBurette',
          infoContent: {
            title: 'Burette Conditioning',
            content: 'Rinse the burette first with a few mL of **deionized water**, then with a few mL of the **~0.10 M NaOH** solution (previously standardized in analytical techniques). Discard both rinses. The NaOH rinse removes water droplets that would dilute the titrant. Rotate to coat all surfaces, then drain through the tip.',
            whatHappens: 'The burette is conditioned first with water then with NaOH solution to remove contaminants and prevent dilution of the titrant.',
            observe: [
              'Rinse first with deionized water, then with NaOH titrant solution',
              'Rotate the burette to coat all inner surfaces during each rinse',
              'Drain rinses through the stopcock tip to condition the entire burette',
            ],
          }
        },
        {
          id: 2,
          title: 'Fill Burette',
          shortDescription: 'Fill burette with ~0.10 M NaOH solution (previously standardized)',
          handler: 'handleFillBurette'
        },
        {
          id: 3,
          title: 'Prime Stopcock',
          shortDescription: 'Remove air bubbles from the tip by opening stopcock briefly',
          handler: 'handlePrimeStopcock',
          infoContent: {
            title: 'Air Bubbles',
            content: 'Air bubbles below the stopcock will be released during titration, causing reading errors. Always check for and remove bubbles before starting.',
            whatHappens: 'The stopcock is briefly opened to flush out any trapped air bubbles from the burette tip, which would cause volume reading errors during titration.',
            observe: [
              'Look for air bubbles trapped in the burette tip below the stopcock',
              'Open the stopcock briefly to flush bubbles out with a fast flow',
              'Verify the tip is completely filled with NaOH solution (no gaps)',
            ],
          }
        },
        {
          id: 4,
          title: 'Record Initial Reading',
          shortDescription: 'Read the meniscus level and record initial burette reading',
          handler: 'handleRecordInitial',
          infoContent: {
            title: 'Reading the Meniscus',
            content: 'Read the bottom of the meniscus with your **eye at the same level as the liquid surface** to avoid parallax error. Record to **0.01 mL**. The burette reads from top (0) to bottom (50 mL). The initial reading may not be exactly 0.00 mL.',
            whatHappens: 'The initial burette reading is recorded by reading the bottom of the meniscus at eye level to 0.01 mL precision.',
            observe: [
              'Position your eye level with the meniscus to avoid parallax error',
              'Read the bottom of the curved meniscus, not the edges',
              'Record to 0.01 mL (the initial reading may not be exactly 0.00)',
            ],
          }
        }
      ]
    },
    {
      id: 3,
      name: 'vinegar_titration',
      title: 'Part III: Titration of Vinegar',
      equipment: ['Burette', '10 mL pipette', '250 mL Erlenmeyer flask', 'Phenolphthalein'],
      initialState: {
        aliquotTransferred: false,
        indicatorAdded: false,
        solutionColor: '#FFFFFF',
        endpointReached: false,
        timerSeconds: 0,
        isPinkPersistent: false,
        trials: []
      },
      steps: [
        {
          id: 1,
          title: 'Transfer Aliquot',
          shortDescription: 'Pipette 10.00 mL of diluted vinegar to each of three 250 mL Erlenmeyer flasks',
          handler: 'handleTransferAliquot',
          infoContent: {
            title: 'Working Aliquot',
            content: 'We titrate 10.00 mL of the diluted solution. This represents 1.0 mL of original vinegar (10.00 mL × 10/100 mL dilution). Prepare three aliquots for triplicate titrations.',
            whatHappens: 'Three 10.00 mL aliquots of the diluted vinegar are pipetted into separate Erlenmeyer flasks for triplicate titrations.',
            observe: [
              'Use the volumetric pipette to transfer exactly 10.00 mL to each flask',
              'Each aliquot represents 1.0 mL of original undiluted vinegar',
              'Label the three flasks (Trial 1, 2, 3) for organized data collection',
            ],
          }
        },
        {
          id: 2,
          title: 'Add Indicator',
          shortDescription: 'Add two drops of phenolphthalein indicator',
          handler: 'handleAddIndicator',
          infoContent: {
            title: 'Phenolphthalein Indicator',
            content: 'Phenolphthalein is colorless in acid (pH < 8.2) and pink in base (pH > 10). The endpoint occurs when the solution just turns pink and stays pink for 30 seconds.',
            whatHappens: 'Two drops of phenolphthalein indicator are added to the acidic vinegar aliquot; the indicator remains colorless in the acidic solution.',
            observe: [
              'The solution remains colorless after adding phenolphthalein (acidic pH)',
              'Two drops of indicator are sufficient for clear endpoint detection',
              'The indicator will turn pink only when excess NaOH is present (pH > 8.2)',
            ],
          }
        },
        {
          id: 3,
          title: 'Begin Titration',
          shortDescription: 'Place flask on white paper/tile and add NaOH dropwise while swirling',
          handler: 'handleBeginTitration',
          infoContent: {
            title: 'Titration Technique',
            content: 'Place a **white piece of paper or tile** under the flask to help observe color changes. Add NaOH slowly while continuously swirling the flask. As you approach the endpoint, pink streaks will appear where the NaOH enters the solution and will disappear upon swirling. Slow down when this happens! Expected NaOH volume: approximately **8-10 mL** per 10 mL aliquot for 5% vinegar.',
            whatHappens: 'NaOH is added from the burette while continuously swirling the flask; the base neutralizes the acetic acid progressively until the equivalence point is reached.',
            observe: [
              'Place a white tile under the flask for better color observation',
              'Pink flashes appear where NaOH drops enter but disappear on swirling',
              'Slow down to drop-by-drop addition when pink streaks persist longer',
            ],
          }
        },
        {
          id: 4,
          title: 'Approach Endpoint',
          shortDescription: 'Add NaOH drop by drop near the endpoint',
          handler: 'handleApproachEndpoint'
        },
        {
          id: 5,
          title: 'Detect Endpoint',
          shortDescription: 'Stop when the first pale pink colour persists for 30 seconds',
          handler: 'handleDetectEndpoint',
          validation: {
            requires: ['isPinkPersistent']
          },
          infoContent: {
            title: 'Endpoint Detection',
            content: 'The endpoint is indicated by the first pale pink colour that persists for 30 seconds. This indicates all the acetic acid has been neutralized.',
            chemicalEquation: 'CH₃COOH + NaOH → NaCH₃COO + H₂O',
            whatHappens: 'The endpoint is reached when one final drop of NaOH causes the solution to turn a pale pink that persists for 30 seconds, indicating all acetic acid has been neutralized.',
            observe: [
              'The pale pink color persists for at least 30 seconds without fading',
              'This indicates a slight excess of NaOH beyond the equivalence point',
              'If the solution turns deep pink/magenta, you have overshot the endpoint',
            ],
          }
        },
        {
          id: 6,
          title: 'Record Final Reading',
          shortDescription: 'Record the final burette reading',
          handler: 'handleRecordFinal'
        },
        {
          id: 7,
          title: 'Repeat Titration',
          shortDescription: 'Perform at least 2 more trials for concordant results',
          handler: 'handleRepeatTitration',
          infoContent: {
            title: 'Concordant Results',
            content: 'Results are concordant when they agree within 0.10 mL. Perform at least 3 trials and average the concordant values.',
            whatHappens: 'Additional titration trials are performed to obtain concordant results (agreeing within 0.10 mL), ensuring statistical reliability of the data.',
            observe: [
              'Refill the burette before each new trial',
              'Compare titre volumes from successive trials',
              'Results are concordant when they agree within 0.10 mL',
            ],
          }
        }
      ]
    },
    {
      id: 4,
      name: 'vinegar_calculations',
      title: 'Part IV: Vinegar Calculations',
      equipment: ['Calculator', 'Data table'],
      initialState: {
        averageVolume: null,
        molesNaOH: null,
        molesAceticAcid: null,
        percentAceticAcid: null
      },
      steps: [
        {
          id: 1,
          title: 'Calculate Average Volume',
          shortDescription: 'Average the concordant NaOH volumes',
          handler: 'handleCalculateAverage'
        },
        {
          id: 2,
          title: 'Calculate Moles NaOH',
          shortDescription: 'Calculate moles of NaOH used',
          handler: 'handleCalculateMoles',
          infoContent: {
            title: 'Moles of NaOH',
            content: 'moles NaOH = Molarity × Volume (L) = 0.10 M × (Volume in mL / 1000)',
            whatHappens: 'The moles of NaOH consumed at the endpoint are calculated from the titrant concentration and the average volume delivered.',
            observe: [
              'Convert the average volume from mL to L by dividing by 1000',
              'Multiply by the exact NaOH molarity (previously standardized)',
              'This gives the moles of NaOH that reacted with the acetic acid',
            ],
          }
        },
        {
          id: 3,
          title: 'Calculate Moles Acetic Acid',
          shortDescription: 'Use 1:1 stoichiometry to find moles CH3COOH',
          handler: 'handleCalculateAcid',
          infoContent: {
            title: 'Stoichiometry',
            content: 'From the balanced equation, 1 mole NaOH reacts with 1 mole CH₃COOH. Therefore, moles CH₃COOH = moles NaOH.',
            whatHappens: 'The 1:1 stoichiometric ratio from the balanced equation means the moles of acetic acid equal the moles of NaOH consumed.',
            observe: [
              'The balanced equation shows a 1:1 mole ratio between CH3COOH and NaOH',
              'Therefore moles of acetic acid = moles of NaOH used',
              'This is valid because acetic acid is monoprotic (one H+ per molecule)',
            ],
          }
        },
        {
          id: 4,
          title: 'Calculate Percent Acetic Acid',
          shortDescription: 'Determine %(m/v) acetic acid in original vinegar',
          handler: 'handleCalculatePercent',
          infoContent: {
            title: 'Percent Calculation',
            content: 'Diluted concentration (M*) = moles acetic acid / 0.010 L. Original concentration = diluted conc x 10. Weight (g) of acetic acid in 1 L = moles/L x 60.05 g/mol. %(m/v) = (grams acetic acid / 1000 mL) x 100. Expected: 4-6% for vinegar.',
            whatHappens: 'The percent acetic acid is calculated by converting moles to grams using the molar mass (60.05 g/mol) and accounting for the dilution factor.',
            observe: [
              'Account for the 10x dilution factor to find the original concentration',
              'Convert moles/L to grams/L using the molar mass of acetic acid (60.05 g/mol)',
              'Expected result is 4-6% (m/v) for commercial vinegar',
            ],
          }
        }
      ]
    },
    {
      id: 5,
      name: 'glacial_acetic',
      title: 'Part V: Determination of Glacial Acetic Acid Concentration',
      equipment: ['10 mL pipette', '100 mL volumetric flask', 'Glacial acetic acid'],
      initialState: {
        sampleType: 'glacial_acetic',
        dilutionComplete: false,
        trials: [],
        percentPurity: null
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Glacial Acetic Acid Sample',
          shortDescription: 'Determine dilution necessary and prepare sample using a volumetric flask',
          handler: 'handlePrepareGlacial',
          infoContent: {
            title: 'Glacial Acetic Acid',
            content: 'Glacial acetic acid is essentially pure acetic acid (~17-18 M). The optimum titre size is about **20 to 30 mL**, so dilution of the concentrated acid will be necessary before titrating with 0.1 M NaOH. Determine the dilution necessary and prepare the sample using a volumetric flask.',
            whatHappens: 'Glacial acetic acid (~17-18 M) is diluted to a concentration suitable for titration with 0.1 M NaOH, targeting a titre volume of 20-30 mL.',
            observe: [
              'Glacial acetic acid is colorless with a strong, sharp vinegar odor',
              'Handle with care as it is corrosive and produces irritating fumes',
              'Calculate the required dilution before preparing the sample',
            ],
          }
        },
        {
          id: 2,
          title: 'Dilute to 100 mL',
          shortDescription: 'Add distilled water to the mark and mix',
          handler: 'handleDiluteGlacial'
        },
        {
          id: 3,
          title: 'Titrate Sample',
          shortDescription: 'Carry out titration and determine the concentration of the glacial acetic acid',
          handler: 'handleTitrateGlacial'
        },
        {
          id: 4,
          title: 'Calculate Concentration',
          shortDescription: 'Determine concentration of glacial acetic acid. Show all calculations and methods of analysis.',
          handler: 'handleCalculatePurity',
          infoContent: {
            title: 'Concentration Analysis',
            content: 'Determine the concentration of the lab-grade glacial acetic acid from your titration data. Show all calculations and methods of analysis in your lab write-up.',
            whatHappens: 'The concentration of the glacial acetic acid is back-calculated from the titration results and the dilution factor used during sample preparation.',
            observe: [
              'Show all calculation steps clearly in your lab write-up',
              'Account for the dilution factor used to prepare the sample',
              'Compare your result to the expected concentration of ~17-18 M',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp9',
    title: 'Acetic Acid Titration Quiz',
    questions: [
      {
        id: 'exp9-q1',
        type: 'mcq' as const,
        question: 'What indicator is used in the titration of acetic acid with NaOH?',
        options: [
          { id: 'a', text: 'Methyl orange' },
          { id: 'b', text: 'Bromothymol blue' },
          { id: 'c', text: 'Phenolphthalein' },
          { id: 'd', text: 'Litmus' },
        ],
        correctOptionId: 'c',
        explanation: 'Phenolphthalein is the preferred indicator because the equivalence point of a weak acid-strong base titration occurs at a pH above 7 (around pH 8-9). Phenolphthalein changes from colorless to pink at approximately pH 8.2, which closely matches this equivalence point.',
      },
      {
        id: 'exp9-q2',
        type: 'mcq' as const,
        question: 'The endpoint of the titration is reached when:',
        options: [
          { id: 'a', text: 'The solution turns deep purple and stays purple' },
          { id: 'b', text: 'A faint persistent pink color lasts for at least 30 seconds' },
          { id: 'c', text: 'The solution becomes completely colorless' },
          { id: 'd', text: 'Bubbles appear in the solution' },
        ],
        correctOptionId: 'b',
        explanation: 'The endpoint is detected when the first faint pink color of phenolphthalein persists for at least 30 seconds after adding a single drop of NaOH. This indicates that a very slight excess of base is present, meaning all the acid has been neutralized.',
      },
      {
        id: 'exp9-q3',
        type: 'mcq' as const,
        question: 'Why is the vinegar sample diluted before titration?',
        options: [
          { id: 'a', text: 'To make the indicator work better' },
          { id: 'b', text: 'To use a manageable volume of NaOH titrant and improve precision' },
          { id: 'c', text: 'To remove impurities from the vinegar' },
          { id: 'd', text: 'To convert acetic acid to sodium acetate' },
        ],
        correctOptionId: 'b',
        explanation: 'Commercial vinegar is approximately 5% acetic acid. Diluting it (e.g., pipetting 10 mL into a 100 mL flask) reduces the amount of acid so that the titration requires a reasonable volume of ~0.10 M NaOH, typically 15-25 mL, which can be measured precisely with a 50 mL burette.',
      },
      {
        id: 'exp9-q4',
        type: 'short_answer' as const,
        question: 'Write the balanced chemical equation for the reaction between acetic acid (CH₃COOH) and sodium hydroxide (NaOH), and explain why this is a 1:1 mole ratio.',
        sampleAnswer: 'CH₃COOH + NaOH → CH₃COONa + H₂O. Acetic acid is a monoprotic acid, meaning each molecule donates exactly one proton (H⁺) to the base. Each NaOH molecule provides one hydroxide ion (OH⁻). Therefore, one mole of acetic acid reacts with exactly one mole of NaOH in a 1:1 stoichiometric ratio.',
      },
      {
        id: 'exp9-q5',
        type: 'calculation' as const,
        question: 'If 22.50 mL of 0.1025 M NaOH is required to titrate a 10.00 mL aliquot of diluted vinegar (dilution factor = 10), what is the percent acetic acid (w/v) in the original vinegar? (Molar mass of CH₃COOH = 60.05 g/mol)',
        expectedValue: 1.385,
        tolerance: 3,
        unit: '% (w/v)',
      },
    ],
  },
};

// Phenolphthalein indicator color data
export const PHENOLPHTHALEIN_COLORS = {
  acidic: {
    color: '#FFFFFF',
    description: 'Colorless (acidic/neutral)',
    pHRange: '< 8.2'
  },
  nearEndpoint: {
    color: '#FFE4E8',
    description: 'Faint pink flash',
    pHRange: '~8.2'
  },
  atEndpoint: {
    color: '#FFB6C1',
    description: 'Pale persistent pink',
    pHRange: '8.2-10.0'
  },
  pastEndpoint: {
    color: '#FF69B4',
    description: 'Deeper pink',
    pHRange: '> 10'
  },
  overshoot: {
    color: '#FF1493',
    description: 'Magenta/dark pink',
    pHRange: '>> 10'
  }
};

// Solution colors
export const SOLUTION_COLORS = {
  vinegar: { color: '#FFF8E7', description: 'Pale amber/colorless' },
  glacialAcetic: { color: '#FFFFFF', description: 'Colorless' },
  naohSolution: { color: '#FFFFFF', description: 'Colorless' },
  withIndicator: { color: '#FFFFFF', description: 'Colorless (acidic)' }
};

// Equipment colors
export const EQUIPMENT_COLORS = {
  buretteGlass: { color: '#E8F4F8', description: 'Clear with blue tint' },
  buretteGraduations: { color: '#0066CC', description: 'Blue markings' },
  stopcock: { color: '#808080', description: 'Gray plastic' },
  volumetricFlask: { color: '#F0F8FF', description: 'Clear glass' },
  erlenmeyerFlask: { color: '#F5F5F5', description: 'Clear glass' }
};

// Titration data helper
export const EXPECTED_VINEGAR_PERCENT = {
  min: 4.0,
  max: 6.0,
  typical: 5.0
};

export default experiment9Config;
